import { combineReducers, applyMiddleware, legacy_createStore, compose } from 'redux';
import ReplaceableMiddleware from 'redux-replaceable-middleware';
import createLogger from 'redux-logger';
import thunkMiddleware from 'redux-thunk';
import createSagaMiddleware from 'redux-saga';
import _ from 'lodash';
import { logger } from '../Services';
import { combineNestedReducers } from './reducer-utils';

import analyticsService from '../../common/Services/AnalyticsService';
import { ckRedux } from 'console-kit';

import analyticsSaga from '../../ConsoleApp/routing/routeTracking';

const _uncombinedReducers = {
  [ckRedux.namespace]: ckRedux.reducer,
};

const analyticsMiddleware = ({ dispatch, getState }) => {
  return next => action => {
    let _track = action;
    if ((_track = _track.meta) && (_track = _track.tracking)) {
      analyticsService.trackAction(_track.event, _track);
    }
    return next(action);
  };
};

/*global process */

/**
 * asynchronously inject a reducer when ever this is called
 * @param      {Redux.store}  store            the redux state tree
 * @param      {String}       name             the name of the reducer - will become the branch off the state tree
 * @param      {Function}     asyncReducer     the reducer to inject
 */
const injectAsyncReducer = (store = defaultStore, name, asyncReducer) => {
  /*
    lifehackett: 6/12/17
    We were seeing a weird bug when injecting a reducer that has already been added.
    This guards against that from happening.
    Reducers were receiving phantom dispatches.
    It seems like only actions that have already been dispatched get called phantomly.
    I'd still like to understand the root cause, but haven't been able to reproduce on my machine.
  */
  if (_.has(_uncombinedReducers, name)) {
    return;
  }
  // you can now pass in dot notation paths to add reducers later.
  // update the cache of reducers with the one just passed in
  _.set(_uncombinedReducers, name, asyncReducer);

  // combine the now updated list of uncombined reducers
  const rootReducer = combineNestedReducers(_uncombinedReducers);

  // replace the reducer in the store
  store.replaceReducer(rootReducer);
};

const removeAsyncReducer = name => {
  if (!_.has(_uncombinedReducers, name)) {
    logger.error(`Attempting to remove unknown reducer: ${name}. Aborting`);
    return;
  }

  _.unset(_uncombinedReducers, name);

  // combine the now updated list of uncombined reducers
  const rootReducer = combineNestedReducers(_uncombinedReducers);

  // replace the reducer in the store
  defaultStore.replaceReducer(rootReducer);
};

const cancelInjectedSaga = asyncSaga => {
  if (sagaMiddlewareTasks[asyncSaga.name]) {
    sagaMiddlewareTasks[asyncSaga.name].cancel();
  }
};
const cancelInjectedSagas = sagas => sagas.forEach(saga => cancelInjectedSaga(saga));

/**
 * check if the user has the dev tools extenstion installed in Chrome or Firefox
 */
const hasDevToolsExtension = () => {
  return (
    typeof window === 'object' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
    ((/Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)) ||
      (/Mozilla\/5\.0/.test(navigator.userAgent) && /Firefox/.test(navigator.userAgent)))
  );
};

function createEnhancer(middlewares = []) {
  // when not in production, if developer has the devtools extension, use them. if not, use standard compose and add the logger
  const composeEnhancers =
    hasDevToolsExtension() === true
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
        // Specify extension’s options like here name, actionsBlacklist, actionsCreators or immutablejs support
      })
      : compose;

  // when in dev, we will log out all actions to the console for easier testing and debugging
  if (process.env.NODE_ENV !== 'production' && !hasDevToolsExtension) {
    middlewares.push(createLogger());
  }

  middlewares.push(analyticsMiddleware);

  return composeEnhancers(applyMiddleware(...middlewares));
}

function injectAsyncMiddleware(middleware) {
  replaceableMiddleware.replaceBy(middleware);
}

// Asynchronously add app sagas to middleware
// https://github.com/yelouafi/redux-saga/releases/tag/v0.7.0
// http://yelouafi.github.io/redux-saga/docs/api/index.html#runsagaiterator-options
function injectAsyncSaga(asyncSaga) {
  // prevent duplicate saga tasks by maintaining a hash of running tasks and canceling as necessary

  // Unnamed functions have name==="_callee"
  if (asyncSaga.name === '_callee') {
    logger.error(
      'Trying to inject a saga without a name. Sagas with the same name will collide so we require a name to minimize the probability of this. Change your saga to a named function- Example: export default function*() => export default function* myCoolSaga()'
    );
  }

  if (sagaMiddlewareTasks[asyncSaga.name]) {
    sagaMiddlewareTasks[asyncSaga.name].cancel();
  }

  const task = sagaMiddleware.run(asyncSaga);
  sagaMiddlewareTasks[asyncSaga.name] = task;
}

function removeAsyncSagas(sagas) {
  _.forEach(sagas, saga => {
    if (!sagaMiddlewareTasks[saga.name]) {
      logger.error(`Attempting to remove unknown saga: ${saga}. Aborting.`);
      return;
    }
    sagaMiddlewareTasks[saga.name].cancel();
  });
}

const injectAsyncSagas = sagas =>
  sagas.forEach(saga => {
    injectAsyncSaga(saga);
  });

/**
 * configure the redux state store by applying the relevant middleware and returning the resulting store
 * @param      {Object}    appState         the initial state with which to populate the store
 * @param      {Array}     middlewares      a list of middlewares to apply to the store; default list includes thunkMiddleware
 * @return     {store}     the redux store
 */
function configureStore(appState, middlewares = [thunkMiddleware]) {
  return legacy_createStore(combineReducers(_uncombinedReducers), appState, createEnhancer(middlewares));
}

export default configureStore;

// Default Redux store used across all of Console platform for new development
const sagaMiddleware = createSagaMiddleware();
const sagaMiddlewareTasks = {};
const replaceableMiddleware = ReplaceableMiddleware();
const defaultStore = configureStore({}, [replaceableMiddleware, sagaMiddleware, thunkMiddleware]);
const getStore = () => defaultStore;
injectAsyncSagas(ckRedux.sagas);
injectAsyncSagas([analyticsSaga]);
// cache an object representing all the reducers before they are combined

export {
  injectAsyncReducer,
  injectAsyncSaga,
  injectAsyncSagas,
  injectAsyncMiddleware,
  getStore,
  cancelInjectedSaga,
  cancelInjectedSagas,
  removeAsyncReducer,
  removeAsyncSagas,
};
